import type { MarketplaceBrowseMapMarkers } from 'components/marketplace/browse/marketplaceBrowse';
import type { LikedPropertyIds } from 'components/marketplace/types';
import type {
  GeoJSONFeature,
  Map as MapboxMap,
  MapMouseEvent,
  SymbolLayerSpecification,
} from 'mapbox-gl';

import {
  DEFAULT_MAP_MARKER_WIDTH,
  LIKED_MAP_MARKER_WIDTH,
  MAP_MARKER_HEIGHT,
  mapMarkerImageId,
} from './mapMarkerImage';

export const MARKETPLACE_MARKER_SOURCE_ID = 'marketplace-map-markers';
export const MARKETPLACE_MARKER_LAYER_ID = 'marketplace-map-marker-symbols';
export const MARKETPLACE_MARKER_HIGHLIGHT_LAYER_ID = 'marketplace-map-marker-highlighted-symbol';

type MapMarkerProperties = {
  addressId: string;
  focusedImageId: string;
  highlightedImageId: string;
  imageId: string;
  isLiked: boolean;
};

function formatMapMarkerPrice(number: number) {
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: number >= 1_000_000 ? 2 : 0,
    notation: 'compact',
    compactDisplay: 'short',
  });
}

type MarkerLabelOptions = {
  /** Whether the listing details request is pending. */
  isLoading: boolean;
  /** Listing price in cents, or null when unavailable. */
  listPriceCents: number | null;
  /** Marketplace listing state used when a price is not yet public. */
  listingState?: string | null;
};

function markerLabel({ isLoading, listPriceCents, listingState }: MarkerLabelOptions) {
  if (isLoading) {
    return '•••';
  }
  if (listPriceCents != null) {
    return formatMapMarkerPrice(listPriceCents / 100);
  }
  return listingState === 'AVAILABLE_SOON' ? 'Soon' : '';
}

type BuildMapMarkerFeatureCollectionOptions = {
  /** Address of the listing whose details are loading. */
  loadingAddressId?: string | null;
  /** Addresses the viewer has favorited. */
  likedPropertyIds?: LikedPropertyIds;
  /** Listings to render in the map viewport. */
  markers?: MarketplaceBrowseMapMarkers | null;
  /** Address of the listing selected in the results. */
  selectedAddressId?: string;
};

export function buildMapMarkerFeatureCollection({
  loadingAddressId,
  likedPropertyIds,
  markers,
  selectedAddressId,
}: BuildMapMarkerFeatureCollectionOptions) {
  const activeAddressId = loadingAddressId ?? selectedAddressId;
  // oxlint-disable-next-line unicorn/no-array-sort -- Cosmos still targets browsers without Array.prototype.toSorted.
  const sortedMarkers = [...(markers ?? [])].sort(
    (a, b) => b.location.latitude - a.location.latitude,
  );

  return {
    type: 'FeatureCollection' as const,
    features: sortedMarkers.map((marker) => {
      const isLiked = likedPropertyIds?.has(marker.addressId) ?? false;
      const isLoading = marker.addressId === loadingAddressId;
      const isHighlighted = marker.addressId === activeAddressId;
      const label = markerLabel({
        isLoading,
        listingState: marker.listingState,
        listPriceCents: marker.listPriceCents,
      });
      return {
        type: 'Feature' as const,
        id: marker.addressId,
        geometry: {
          type: 'Point' as const,
          coordinates: [marker.location.longitude, marker.location.latitude],
        },
        properties: {
          addressId: marker.addressId,
          focusedImageId: mapMarkerImageId({
            hasFocusRing: true,
            isHighlighted: true,
            isLiked,
            isLoading,
            label,
          }),
          highlightedImageId: mapMarkerImageId({
            hasFocusRing: false,
            isHighlighted: true,
            isLiked,
            isLoading,
            label,
          }),
          imageId: mapMarkerImageId({
            hasFocusRing: false,
            isHighlighted,
            isLiked,
            isLoading,
            label,
          }),
          isLiked,
        } satisfies MapMarkerProperties,
      };
    }),
  };
}

export const marketplaceMarkerLayer: SymbolLayerSpecification = {
  id: MARKETPLACE_MARKER_LAYER_ID,
  type: 'symbol',
  source: MARKETPLACE_MARKER_SOURCE_ID,
  layout: {
    'symbol-placement': 'point',
    'symbol-z-order': 'source',
    'icon-image': ['get', 'imageId'],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'viewport',
  },
};

export const marketplaceHighlightedMarkerLayer: SymbolLayerSpecification = {
  ...marketplaceMarkerLayer,
  id: MARKETPLACE_MARKER_HIGHLIGHT_LAYER_ID,
  layout: {
    ...marketplaceMarkerLayer.layout,
    'icon-image': ['get', 'highlightedImageId'],
  },
};

type MarkerQueryMap = Pick<MapboxMap, 'getLayer' | 'project' | 'queryRenderedFeatures'>;

type MarkerFeature = GeoJSONFeature & {
  geometry?: {
    coordinates?: unknown;
    type?: string;
  } | null;
  id?: string | number;
  properties?: Record<string, unknown> | null;
};

type MarkerPillHitOptions = {
  /** Pointer event whose screen coordinate is being tested. */
  event: MapMouseEvent;
  /** Rendered marker feature under the pointer. */
  feature: MarkerFeature;
  /** Map projection used to resolve the marker's screen position. */
  map: MarkerQueryMap;
};

function isMarkerPillHit({ event, feature, map }: MarkerPillHitOptions) {
  if (feature.geometry?.type !== 'Point') {
    return true;
  }
  const coordinates = feature.geometry.coordinates;
  if (!Array.isArray(coordinates)) {
    return true;
  }
  const longitude: unknown = coordinates[0];
  const latitude: unknown = coordinates[1];
  if (
    typeof longitude !== 'number' ||
    !Number.isFinite(longitude) ||
    typeof latitude !== 'number' ||
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90
  ) {
    return true;
  }
  const center = map.project([longitude, latitude]);
  const halfWidth =
    (feature.properties?.isLiked ? LIKED_MAP_MARKER_WIDTH : DEFAULT_MAP_MARKER_WIDTH) / 2;
  const halfHeight = MAP_MARKER_HEIGHT / 2;
  const distanceX = Math.abs(event.point.x - center.x);
  const distanceY = Math.abs(event.point.y - center.y);
  if (distanceX > halfWidth || distanceY > halfHeight) {
    return false;
  }
  const cornerX = Math.max(0, distanceX - (halfWidth - halfHeight));
  return cornerX * cornerX + distanceY * distanceY <= halfHeight * halfHeight;
}

type MarketplaceMarkerAddressIdAtPointOptions = {
  /** Pointer event whose screen coordinate is being queried. */
  event: MapMouseEvent;
  /** Map containing the marketplace marker layers. */
  map: MarkerQueryMap;
};

export function marketplaceMarkerAddressIdAtPoint({
  event,
  map,
}: MarketplaceMarkerAddressIdAtPointOptions) {
  const layers = [MARKETPLACE_MARKER_HIGHLIGHT_LAYER_ID, MARKETPLACE_MARKER_LAYER_ID].filter(
    (layerId) => map.getLayer(layerId),
  );
  if (layers.length === 0) {
    return null;
  }
  const features = map.queryRenderedFeatures(event.point, { layers });
  for (const feature of features) {
    const markerFeature: MarkerFeature = feature;
    if (isMarkerPillHit({ event, feature: markerFeature, map })) {
      const addressId: unknown = markerFeature.properties?.addressId ?? markerFeature.id;
      if (typeof addressId === 'string') {
        return addressId;
      }
    }
  }
  return null;
}
